import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type Currency = components["schemas"]["CurrencyResponse"];

interface CurrencyPickerProps {
  value: string;
  onChange: (value: string) => void;
  currencies?: Currency[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

export function CurrencyPicker({
  value,
  onChange,
  currencies: providedCurrencies,
  placeholder = "Select a currency...",
  disabled,
  id,
}: CurrencyPickerProps) {
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState<Currency[]>([]);
  const fetchStarted = useRef(false);

  useEffect(() => {
    if (providedCurrencies || fetchStarted.current) return;
    fetchStarted.current = true;
    async function fetchCurrencies() {
      const { data } = await api.GET("/v1/currencies/available");
      if (data) setFetched(data);
    }
    fetchCurrencies();
  }, [providedCurrencies]);

  const currencies = providedCurrencies ?? fetched;
  const selected = currencies.find(
    (c) => c.code.toUpperCase() === value.toUpperCase(),
  );

  // modal: these pickers sit inside Dialogs, whose scroll lock swallows wheel events on the portaled list.
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">
              {selected.name} ({selected.code})
            </span>
          ) : value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
      >
        <Command>
          <CommandInput placeholder="Search currencies..." />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            <CommandGroup>
              {currencies.map((currency) => (
                <CommandItem
                  key={currency.code}
                  value={`${currency.code} ${currency.name}`}
                  onSelect={() => {
                    onChange(currency.code.toUpperCase());
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 size-4",
                      selected?.code === currency.code
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {currency.name}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({currency.code})
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
